/**
 * Agent-domain types mirrored from the backend (services/agent-factory +
 * packages/agent-core schemas). Raw Mongoose documents serialize `_id`;
 * responses are wrapped in `{ data: ... }` — `utils.docId` / the api slice
 * normalize both. No backend package is imported (P1 app convention).
 */
import type { ComponentType } from 'react';

/** `AgentTemplateKind` — the five values of the closed enum (enums.ts:119-125). */
export const AGENT_TEMPLATE_KINDS = [
  'org-manager',
  'assistant',
  'audio-assistant',
  'coder',
  'custom',
] as const;

export type AgentTemplateKind = (typeof AGENT_TEMPLATE_KINDS)[number];

/**
 * `AgentTemplateWizardStepType` — the CLOSED, FINAL 10-type vocabulary
 * (enums.ts:133-144, frozen by P2-T2). P4/P5 add renderers, never new types.
 */
export const AGENT_TEMPLATE_WIZARD_STEP_TYPES = [
  'text',
  'select',
  'attach-storage',
  'attach-policy',
  'attach-webhook',
  'forwarding',
  'bind-number',
  'select-image',
  'attach-repository',
  'attach-secrets',
] as const;

export type AgentTemplateWizardStepType = (typeof AGENT_TEMPLATE_WIZARD_STEP_TYPES)[number];

/** `TemplateWizardStep` (`agent-template.schema.ts:123-143`). */
export interface TemplateWizardStep {
  id: string;
  title: string;
  type: AgentTemplateWizardStepType;
  required: boolean;
  options?: Record<string, unknown>;
}

/** Statement shape inside a template `policySpecs` entry (plural arrays). */
export interface TemplatePolicyStatement {
  effect: 'Allow' | 'Deny';
  actions: string[];
  resources: string[];
}

export interface TemplatePolicySpec {
  name: string;
  description?: string;
  statements: TemplatePolicyStatement[];
}

export interface TemplateMemoryDefaults {
  attachStorageMemory?: boolean;
}

export interface AgentTemplate {
  id?: string;
  _id?: string;
  name: string;
  description?: string;
  kind: AgentTemplateKind;
  defaultInstructions?: string;
  policySpecs: TemplatePolicySpec[];
  toolConfig?: Record<string, unknown>;
  memory?: TemplateMemoryDefaults;
  wizardSteps: TemplateWizardStep[];
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** `GET /agent/model-collections` view (`model-collections.service.ts:14-25`). */
export interface ModelCollectionEntry {
  modelConfigId: string;
  weight?: number;
  position?: number;
}

export interface ModelCollection {
  id: string;
  name: string;
  description?: string;
  strategy: 'position' | 'weight' | 'random';
  entries: ModelCollectionEntry[];
  failover?: { enabled: boolean; maxAttemptsPerRun?: number };
  ownerUserId?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Read view of a factory (`factories.service.ts` `FactoryView`, trimmed to UI needs). */
export interface AgentFactoryView {
  id: string;
  name: string;
  description?: string;
  ownerUserId: string;
  projectId?: string;
  orgId?: string;
  instructions: string;
  modelCollectionId: string;
  policyIds: string[];
  memory: Record<string, unknown>;
  memoryPrincipalUserId?: string;
  /**
   * P6-T3: persisted coder workspace selections (wizard select-image /
   * attach-repository / attach-secrets). Present on factories created through
   * the wizard; undefined on every other factory (backward compatible read —
   * mirrors `FactoryView.coderConfig`, factories.service.ts).
   */
  coderConfig?: AgentCoderConfig;
  tags?: string[];
  modelType?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** `TriggerKind` (enums.ts:93-97) + `TriggerBindingView` (`triggers.service.ts:12-24`). */
export type TriggerKind = 'cron' | 'webhook' | 'event';

export interface TriggerEventFilter {
  channel: string;
  eventTypes: string[];
}

export interface TriggerBindingView {
  id: string;
  factoryId: string;
  kind: TriggerKind;
  webhookId: string;
  /** Only on the POST response — never re-exposed by GET (once-only capture). */
  webhookUrl?: string;
  cron?: string;
  eventFilter?: TriggerEventFilter[];
  payloadTemplate?: Record<string, unknown>;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** `CreateFactoryTriggerDto` (`dto/create-factory-trigger.dto.ts:14-23`) — no `factoryId`. */
export interface CreateFactoryTriggerBody {
  kind: TriggerKind;
  cron?: string;
  eventFilter?: TriggerEventFilter[];
  payloadTemplate?: Record<string, unknown>;
}

/**
 * Mirror of `TemplateForwardingCfg` (`agent-core` agent-template.schema.ts:
 * 97-114) — same shape `numbers/types.ts:101-107` mirrors. Kept local so
 * `features/agents` never runtime-imports `features/numbers` (the numbers
 * feature already depends on agents; a reverse runtime import would cycle).
 */
export interface WizardForwardingOverride {
  enabled?: boolean;
  audioOut?: { enabled?: boolean; voiceId?: string };
  bindNumberRequired?: boolean;
  greeting?: string;
}

/**
 * Mirror of `FactoryCoderConfigCfg` (agent-core agent-factory.schema.ts,
 * P6-T3) — the coder wizard's select-image / attach-repository / attach-secrets
 * selections, persisted onto the factory. Kept local on purpose (draft.ts
 * convention: the frame must not runtime-depend on `features/coder`).
 * Ids/vault REFERENCE only — secret values never live on the factory.
 */
export interface AgentCoderConfig {
  imageId?: string;
  repositoryId?: string;
  branchName?: string;
  secretsVaultId?: string;
}

/** `FromTemplateDto` (`dto/from-template.dto.ts:33-45`). */
export interface FromTemplateBody {
  templateId: string;
  name: string;
  orgId?: string;
  projectId?: string;
  overrides?: {
    instructions?: string;
    modelCollectionId?: string;
    toolConfig?: Record<string, unknown>;
    /**
     * `forwarding` step value merged at create (OQ-3, numbers/types.ts:109-119
     * — the "consumed P2-side" contract). Present only when the user touched
     * the step; omitting it lets the template default stand.
     */
    forwarding?: WizardForwardingOverride;
    /**
     * P6-T3: coder step selections (select-image / attach-repository /
     * attach-secrets) persisted onto the factory `coderConfig`
     * (dto/from-template.dto.ts). Present only when a coder step holds a
     * value; absent → the template's select-image options.defaultImage seeds
     * `imageId` (factory-instantiation.service.ts).
     */
    coderConfig?: AgentCoderConfig;
  };
}

export interface FromTemplateResult {
  factory: AgentFactoryView;
  templateId: string;
  craftedPolicyIds: string[];
  grantsChecked: number;
}

export interface MemoryAttachResult {
  factoryId: string;
  principalUserId: string;
  policyIds: string[];
  collectionIds: string[];
}

export interface AttachPoliciesResult {
  factoryId: string;
  craftedPolicyIds: string[];
  minted: number;
  reused: number;
  grantsChecked: number;
}

export interface RevokePolicyResult {
  factoryId: string;
  policyId: string;
  deactivated: boolean;
  policyIds: string[];
}

/* ------------------------------------------------------------------ */
/* Wizard contracts (design §7.3, §8)                                  */
/* ------------------------------------------------------------------ */

/** One user-entered grant row (`{effect, action, resource}` → wrapped at submit). */
export interface GrantStatementDraft {
  effect: 'Allow' | 'Deny';
  action: string;
  resource: string;
}

/** One trigger intent — mapped onto `CreateFactoryTriggerBody` at attach time. */
export interface TriggerDraft {
  kind: TriggerKind;
  cron?: string;
  channel?: string;
  eventTypes?: string[];
}

export interface WizardDraft {
  basics: { name: string; instructions: string };
  model: { modelCollectionId: string | null };
  /** attach-storage intent (applied post-create). */
  memory: { attach: boolean };
  /** attach-policy intent (applied post-create as one `wizard-extra-grants` spec). */
  policies: { statements: GrantStatementDraft[] };
  /** attach-webhook intent (applied post-create, one POST per item). */
  triggers: { items: TriggerDraft[] };
  /**
   * Generic per-step values keyed by entry. `text`/`select` stay advisory
   * (§10.3/§25 Q3); the frame now consumes `forwarding` (create
   * `overrides.forwarding`, OQ-3), `bind-number` (post-create bind) and shows
   * every registered type's value on Review (P5 §25 Q2) — see draft.ts.
   */
  steps: Record<string, unknown>;
  projectId?: string;
}

export type StepErrors = Record<string, string>;

/**
 * `WizardStepProps` — the renderer contract (design §8). The doc's five props
 * are kept verbatim; `onErrors` is the setter the §7.4 blur-validation needs
 * ("errors … set by the renderer") — renderers never own error state, the frame does.
 */
export interface WizardStepProps {
  step: TemplateWizardStep;
  template: AgentTemplate;
  value: unknown;
  onChange: (next: unknown) => void;
  errors: StepErrors;
  onErrors: (next: StepErrors) => void;
  orgId: string;
}

/** Each `wizard-steps/*.step.tsx` module exports exactly this shape. */
export interface WizardStepModule {
  type: AgentTemplateWizardStepType;
  component: ComponentType<WizardStepProps>;
}

/* ------------------------------------------------------------------ */
/* Attach-sequence view state (design §15.3)                           */
/* ------------------------------------------------------------------ */

export type AttachItemStatus = 'pending' | 'running' | 'done' | 'error';

export interface AttachItemState {
  id: string;
  label: string;
  status: AttachItemStatus;
  outcome?: string;
  denial?: SubsetDenial;
  /** triggers item: index to resume from on retry. */
  cursor?: number;
}

/** Parsed `assigner_lacks_capability` 403 (design §13.3). */
export interface SubsetDenial {
  message?: string;
  pairs: { action: string; resource: string }[];
}
